import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Microphone, Brain, PlugsConnected, ShieldCheck,
  ChartLineUp, LinkedinLogo, Lightning, Books, ArrowUpRight, Quotes,
} from "@phosphor-icons/react";
import KoshMark from "../components/KoshMark";
import useSEO from "../lib/useSEO";
import { api, mediaUrl } from "../lib/api";

/**
 * /kosh — Sales landing page for KOSH.
 * KOSH is Wisewit's copilot product. This page turns the site demo into a
 * pitchable asset: what it is, how it works, what it costs (per-engagement),
 * and a strong CTA to open KOSH itself (proof-in-the-eating).
 */
export default function KoshLanding() {
  const canonical = typeof window !== "undefined" ? `${window.location.origin}/kosh` : "https://wisewit.ai/kosh";
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/case-studies").then(({ data }) => {
      if (!cancelled) {
        // Only surface deployments explicitly flagged as KOSH-featuring, so the
        // page doesn't dilute with unrelated case studies.
        const koshOnly = (data || []).filter((s) => s.kosh_involvement === true && s.published !== false);
        setStudies(koshOnly.slice(0, 3));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useSEO({
    title: "KOSH — Industrial LLM copilot by Wisewit",
    description:
      "KOSH is Wisewit's production-ready industrial LLM copilot — grounded RAG over your SOPs, live tool-use over MES and historians, voice-first for the plant floor, audit-clean by default. Deployed as a scoped engagement, not a per-seat license.",
    canonical,
    ogImage: `${canonical.replace(/\/kosh$/, "")}/og-kosh.png`,
    ogType: "product",
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "KOSH",
      "alternateName": "KOSH by Wisewit",
      "applicationCategory": "BusinessApplication",
      "applicationSubCategory": "Industrial LLM copilot",
      "operatingSystem": "Web, Edge, On-prem",
      "url": canonical,
      "description":
        "Production-ready industrial LLM copilot: grounded RAG over plant SOPs, live tool-use over MES and historians, voice front-end, per-operator memory, audit-clean logging (21 CFR Part 11 friendly).",
      "featureList": [
        "Grounded RAG over your SOPs and manuals",
        "Read-only + operator-approved write-back over MES / historians / alarm systems",
        "Voice front-end (Whisper-class STT + low-latency TTS)",
        "Per-operator persistent memory across shifts",
        "21 CFR Part 11 friendly audit trail",
        "Demand-signal dashboard on top questions and deflections",
        "Multi-language: English, Hindi (Marathi / Gujarati / Kannada roadmap)",
      ],
      "brand": { "@type": "Brand", "name": "Wisewit" },
      "provider": {
        "@type": "Organization",
        "name": "Wisewit",
        "url": "https://wisewit.ai",
      },
      "offers": [
        {
          "@type": "Offer",
          "name": "Pilot",
          "description": "6–8 week fixed-fee pilot on one production line, one use case, ≤3 tools.",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "USD",
          "price": "0",
          "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "USD", "price": "Fixed-fee, scoped per engagement" },
        },
        {
          "@type": "Offer",
          "name": "Plant",
          "description": "10–14 week full plant rollout: all shifts, all lines, voice + text, write-back with approval.",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "USD",
          "price": "0",
          "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "USD", "price": "Scoped per plant" },
        },
        {
          "@type": "Offer",
          "name": "Enterprise",
          "description": "Multi-site: central KOSH brain + edge instances, cross-plant analytics, custom fine-tuning.",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "USD",
          "price": "0",
          "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "USD", "price": "Custom, multi-site" },
        },
      ],
    },
  });

  return (
    <div data-testid="kosh-landing" className="px-6 md:px-14 py-16 md:py-24">
      {/* ---------- Hero ---------- */}
      <section className="max-w-6xl">
        <div className="text-overline mb-4">◢ KOSH · Wisewit copilot</div>
        <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="w-24 h-24 shrink-0 flex items-center justify-center"
            style={{ background: "#FF4500", borderRadius: "9999px", boxShadow: "0 12px 40px rgba(255,69,0,0.35)" }}
          >
            <KoshMark size={54} tone="on-color" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-display font-bold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            >
              A copilot that
              <br />
              <span className="text-[#FF4500]">knows your floor.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 max-w-2xl text-[#8E8E93] text-lg leading-relaxed"
            >
              KOSH — Sanskrit for <em className="text-white not-italic">vessel of knowledge</em> — is a
              production-ready LLM copilot Wisewit deploys inside your operations. It reads your SOPs,
              queries your MES, remembers each operator, and speaks. Try the demo on this site —
              you've been talking to one KOSH instance the whole time.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("kosh:open"))}
                data-testid="kosh-try-cta"
                className="btn-primary"
              >
                <KoshMark size={16} tone="on-color" /> Try KOSH here now
              </button>
              <Link to="/contact" data-testid="kosh-scope-cta" className="btn-secondary">
                Scope KOSH for your plant <ArrowRight size={14} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------- Feature grid ---------- */}
      <section className="mt-24 max-w-6xl">
        <div className="text-overline mb-6">◢ What ships in every KOSH deployment</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          <Feature
            icon={<Brain size={22} color="#DFFF00" weight="duotone" />}
            title="Grounded RAG"
            body="Retrieval over your SOPs, manuals, historian dumps — never invents. Sources cited on every reply."
            testid="kosh-feat-rag"
          />
          <Feature
            icon={<PlugsConnected size={22} color="#00E5FF" weight="duotone" />}
            title="Tool use over live plant"
            body="Read-only queries into MES, historians, alarm systems. Write-back only with operator approval."
            testid="kosh-feat-tools"
          />
          <Feature
            icon={<Microphone size={22} color="#FF4500" weight="duotone" />}
            title="Voice front-end"
            body="Whisper-class STT + low-latency TTS. Push-to-talk handsets so it works with gloves on."
            testid="kosh-feat-voice"
          />
          <Feature
            icon={<Books size={22} color="#DFFF00" weight="duotone" />}
            title="Per-operator memory"
            body="Every operator gets a persistent session — KOSH remembers context across shifts, not just messages."
            testid="kosh-feat-memory"
          />
          <Feature
            icon={<ShieldCheck size={22} color="#00E5FF" weight="duotone" />}
            title="Audit-clean by default"
            body="Every prompt, tool call, and reply logged. 21 CFR Part 11 friendly. Redaction pipeline for PII."
            testid="kosh-feat-audit"
          />
          <Feature
            icon={<ChartLineUp size={22} color="#FF6B35" weight="duotone" />}
            title="Demand-signal dashboard"
            body="Which questions get asked, which get deflected, which lead to escalation. KOSH is also a listening post."
            testid="kosh-feat-dash"
          />
        </div>
      </section>

      {/* ---------- Live demo strip ---------- */}
      <section className="mt-24 max-w-6xl">
        <div className="ww-card p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center gap-6">
          <span
            className="flex items-center justify-center w-16 h-16 shrink-0"
            style={{ background: "#FF4500", borderRadius: "9999px" }}
          >
            <KoshMark size={30} tone="on-color" />
          </span>
          <div className="flex-1">
            <div className="text-overline mb-2">◢ You're already using it</div>
            <h2 className="font-display text-2xl md:text-3xl text-white">
              This site's copilot IS a KOSH instance.
            </h2>
            <p className="mt-3 text-[#8E8E93] text-[15px] max-w-2xl">
              The floating orange KOSH mark bottom-right? Same engine, deployed against Wisewit's own
              knowledge base. Ask it about MES integration, about a case study, about pricing — the same
              behaviour ships on your floor.
            </p>
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("kosh:open"))}
            data-testid="kosh-demo-open"
            className="btn-primary"
          >
            Open KOSH <Lightning size={14} weight="fill" />
          </button>
        </div>
      </section>

      {/* ---------- KOSH in the wild (case studies) ---------- */}
      <section className="mt-24 max-w-6xl" data-testid="kosh-in-wild">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div className="text-overline">◢ KOSH in the wild</div>
          <Link to="/#deployments" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93] hover:text-[#FF4500] transition-colors">
            All deployments →
          </Link>
        </div>
        {studies.length === 0 ? (
          <div className="border border-dashed border-[#2A2A2A] p-8 text-center">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">◢ Field deployments landing soon</div>
            <p className="mt-3 text-[#D7D7D9] max-w-xl mx-auto text-sm">
              KOSH is currently rolling out with pilot customers under NDA. We'll publish full case
              studies once the deployments hit steady-state. Meanwhile you can already talk to a
              production KOSH instance right here on this site.
            </p>
            <div className="mt-5">
              <Link to="/contact" className="btn-secondary text-[11px] py-2 px-3">
                Be a pilot partner <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Featured testimonial — first case study that has a quote */}
            {(() => {
              const featured = studies.find((s) => s.testimonial_quote);
              if (!featured) return null;
              return (
                <blockquote
                  className="border-l-2 border-[#DFFF00] pl-6 md:pl-8 py-3 mb-10 max-w-4xl"
                  data-testid="kosh-wild-testimonial"
                >
                  <Quotes size={26} color="#DFFF00" weight="fill" className="mb-3" />
                  <p className="font-display text-2xl md:text-[30px] text-white leading-snug">
                    {featured.testimonial_quote}
                  </p>
                  {featured.testimonial_author && (
                    <footer className="mt-4 font-mono text-[10px] tracking-[0.22em] uppercase text-[#8E8E93]">
                      — {featured.testimonial_author}
                      {featured.slug && (
                        <>
                          <span className="mx-2 text-[#333]">·</span>
                          <Link to={`/case-studies/${featured.slug}`} className="text-[#FF4500] hover:text-[#DFFF00]">
                            read the deployment →
                          </Link>
                        </>
                      )}
                    </footer>
                  )}
                </blockquote>
              );
            })()}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
              {studies.map((cs) => (
                <Link
                  key={cs.id}
                  to={cs.slug ? `/case-studies/${cs.slug}` : "/#deployments"}
                  data-testid={`kosh-case-${cs.id}`}
                  className="group bg-[#050505] p-7 hover:bg-[#0a0a0a] transition-colors relative"
                >
                  {cs.cover_image && (
                    <div className="mb-5 -mx-7 -mt-7 h-40 overflow-hidden bg-[#0a0a0a]">
                      <img
                        src={mediaUrl(cs.cover_image)}
                        alt={cs.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#DFFF00]">
                    {cs.industry || "Industrial"}
                  </div>
                  <div className="mt-2 font-display text-xl text-white group-hover:text-[#FF4500] transition-colors">
                    {cs.title}
                  </div>
                  {cs.outcome && (
                    <p className="mt-2 text-sm text-[#8E8E93] line-clamp-3">{cs.outcome}</p>
                  )}
                  <div className="mt-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#FF4500] opacity-0 group-hover:opacity-100 transition-opacity">
                    Read case <ArrowUpRight size={12} weight="bold" />
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      {/* ---------- Deployment tiers ---------- */}
      <section className="mt-24 max-w-6xl">
        <div className="text-overline mb-6">◢ How we ship it</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          <Tier
            name="Pilot"
            duration="6–8 weeks"
            desc="One line, one use case, ≤ 3 tools, single voice or text front-end. Fixed-fee."
            highlights={["RAG over your top 20 SOPs", "1 read-only tool (MES or historian)", "10 named operators", "White-glove onboarding"]}
            testid="kosh-tier-pilot"
          />
          <Tier
            name="Plant"
            duration="10–14 weeks"
            desc="Full plant rollout. All shifts, all lines, voice + text, write-back with approval flow."
            highlights={["RAG over full document store", "5–8 tools incl. write-back", "Unlimited operators", "24/7 SLA on inference"]}
            featured
            testid="kosh-tier-plant"
          />
          <Tier
            name="Enterprise"
            duration="4–6 months"
            desc="Multi-site. Central KOSH brain + edge instances, cross-plant analytics, custom integrations."
            highlights={["Multi-tenant governance", "Edge deployment options", "Custom model fine-tuning", "Dedicated MLOps"]}
            testid="kosh-tier-enterprise"
          />
        </div>
        <div className="mt-6 font-mono text-[10px] text-[#555]">
          ◢ Every KOSH engagement is scoped, not licensed. We build the system, hand you the keys, and
          agree ongoing support terms based on your ops profile.
        </div>
      </section>

      {/* ---------- Bottom CTA ---------- */}
      <section className="mt-24 max-w-4xl">
        <div className="ww-card p-8 md:p-12">
          <div className="text-overline mb-3">◢ Bring KOSH to your operation</div>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
            15-minute discovery.
            <br />
            <span className="text-[#FF4500]">No deck, no fluff.</span>
          </h2>
          <p className="mt-4 text-[#8E8E93] max-w-xl">
            Tell us what you'd want a KOSH instance to know, do, and reply with. We'll come back with a
            reference architecture and a fixed pilot quote.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="kosh-bottom-cta" className="btn-primary">
              Scope your KOSH <ArrowRight size={14} />
            </Link>
            <a
              href="https://www.linkedin.com/company/wisewit-solutions/"
              target="_blank"
              rel="noreferrer"
              className="btn-secondary"
            >
              <LinkedinLogo size={14} weight="fill" color="#DFFF00" /> Follow the build
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Feature({ icon, title, body, testid }) {
  return (
    <div className="bg-[#050505] p-7 ww-trace" data-testid={testid}>
      {icon}
      <div className="mt-4 font-display text-lg text-white">{title}</div>
      <div className="mt-2 text-sm text-[#8E8E93] leading-relaxed">{body}</div>
    </div>
  );
}

function Tier({ name, duration, desc, highlights, featured, testid }) {
  return (
    <div
      className={`bg-[#050505] p-7 relative ${featured ? "outline outline-1 outline-[#FF4500] z-10" : ""}`}
      data-testid={testid}
    >
      {featured && (
        <div className="absolute -top-3 left-6 px-2 py-0.5 bg-[#FF4500] font-mono text-[9px] tracking-[0.24em] uppercase text-white">
          Most common
        </div>
      )}
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#DFFF00]">{duration}</div>
      <div className="mt-2 font-display text-2xl text-white">{name}</div>
      <p className="mt-2 text-sm text-[#8E8E93]">{desc}</p>
      <ul className="mt-4 space-y-2">
        {highlights.map((h) => (
          <li key={h} className="text-sm text-[#D7D7D9] flex items-start gap-2">
            <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: featured ? "#FF4500" : "#DFFF00" }} />
            <span>{h}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
